import CloseIcon from "@mui/icons-material/Close"
import { Box, Button, Drawer, IconButton, Stack, Typography } from "@mui/material"
import { type FC, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { InAppFeedItem } from "./InAppFeedItem"
import { storeSeen } from "./inAppLocalState"
import {
  inAppMessagesActions,
  selectFeedUnreadIds,
  selectVisibleInAppMessages,
} from "./inAppMessagesSlice"

const FEED_DRAWER_WIDTH = 380

type Props = {
  open: boolean
  onClose: () => void
}

/**
 * The What's-new drawer (LOCKER-485): the engine's `feed` slot as the engine orders it.
 * Opening it marks nothing read; only "Mark all read" writes `iam:sl2:seen`.
 */
export const InAppFeedDrawer: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const feed = useAppSelector(selectVisibleInAppMessages).feed
  const unreadIds = useAppSelector(selectFeedUnreadIds)

  const markAllRead = useCallback(() => {
    const ids = feed.map((m) => m.id)
    const at = new Date().toISOString()
    storeSeen(ids, at)
    dispatch(inAppMessagesActions.feedMarkedRead({ ids, at }))
  }, [dispatch, feed])

  return (
    <Drawer
      data-ui="UI-SL2W-290"
      open={open}
      onClose={onClose}
      anchor="right"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        role: "dialog",
        "aria-label": t("inAppFeedTitle"),
        sx: { width: FEED_DRAWER_WIDTH, maxWidth: "100vw" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t("inAppFeedTitle")}
        </Typography>
        {/* Never disabled: a button that disables itself drops focus out of the drawer, and Escape stops closing it. */}
        <Button size="small" onClick={markAllRead}>
          {t("inAppFeedMarkAllRead")}
        </Button>
        <IconButton size="small" aria-label={t("close")} title={t("close")} onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      {feed.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {t("inAppFeedEmpty")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, overflowY: "auto" }}>
          {feed.map((message) => (
            <InAppFeedItem
              key={message.id}
              message={message}
              unread={unreadIds.includes(message.id)}
              onNavigate={onClose}
            />
          ))}
        </Box>
      )}
    </Drawer>
  )
}
